import React from 'react';
import {
    X,
    GraduationCap,
    Sparkles,
    Camera,
    Package,
    Tag,
    Lightbulb,
    Target,
    MessageSquare,
    Heart,
    Users,
    Zap,
    TrendingUp,
    Info,
    ChevronRight
} from 'lucide-react';
import { AWARENESS_STAGES, FUNNEL_STAGES } from '../../services/pillarService';

/**
 * Icon mapping for pillar icons
 */
const ICON_MAP = {
    GraduationCap,
    Sparkles,
    Camera,
    Package,
    Tag,
    Lightbulb,
    Target,
    MessageSquare,
    Heart,
    Users,
    Zap,
    TrendingUp
};

/**
 * PillarContextBanner Component
 * Displays the current pillar context at the top of Creative Chat
 * Shows pillar info, funnel stage, and content guidelines
 *
 * @param {Object} props
 * @param {Object} props.pillar - The selected pillar
 * @param {string} props.funnelStage - Optional funnel stage context (TOFU/MOFU/BOFU)
 * @param {function} props.onChangePillar - Callback to change pillar
 * @param {function} props.onClearPillar - Callback to clear pillar selection
 * @param {boolean} props.expanded - Whether to show expanded view with guidelines
 * @param {function} props.onToggleExpanded - Callback to toggle expanded state
 */
const PillarContextBanner = ({
    pillar,
    funnelStage,
    onChangePillar,
    onClearPillar,
    expanded = false,
    onToggleExpanded
}) => {
    if (!pillar) return null;

    const IconComponent = ICON_MAP[pillar.icon] || Lightbulb;

    // Get funnel stages for this pillar
    const activeFunnelStages = Object.entries(pillar.funnelMapping || {})
        .filter(([, config]) => config.recommended)
        .map(([stage]) => stage);

    // Get awareness stages for this pillar
    const activeAwarenessStages = Object.entries(pillar.awarenessMapping || {})
        .filter(([, config]) => config.recommended)
        .map(([stage]) => ({
            key: stage,
            ...AWARENESS_STAGES[stage]
        }));

    return (
        <div style={{
            background: `linear-gradient(135deg, ${pillar.color}08, ${pillar.color}15)`,
            borderLeft: `4px solid ${pillar.color}`,
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            marginBottom: 'var(--space-4)'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-3) var(--space-4)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    {/* Icon */}
                    <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: 'var(--radius-md)',
                        background: `${pillar.color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <IconComponent size={18} style={{ color: pillar.color }} />
                    </div>

                    {/* Info */}
                    <div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)'
                        }}>
                            <span style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }}>
                                Creating for
                            </span>
                            <span style={{
                                fontWeight: 600,
                                color: pillar.color
                            }}>
                                {pillar.name}
                            </span>
                        </div>

                        {/* Funnel stage badges */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            marginTop: '2px'
                        }}>
                            {funnelStage ? (
                                <span
                                    style={{
                                        fontSize: '10px',
                                        padding: '1px 6px',
                                        background: funnelStage === 'TOFU' ? 'var(--info-100)' :
                                            funnelStage === 'MOFU' ? 'var(--warning-100)' :
                                                'var(--success-100)',
                                        color: funnelStage === 'TOFU' ? 'var(--info-700)' :
                                            funnelStage === 'MOFU' ? 'var(--warning-700)' :
                                                'var(--success-700)',
                                        borderRadius: 'var(--radius-sm)',
                                        fontWeight: 500
                                    }}
                                >
                                    {funnelStage} - {FUNNEL_STAGES[funnelStage]?.description}
                                </span>
                            ) : (
                                activeFunnelStages.map(stage => (
                                    <span
                                        key={stage}
                                        style={{
                                            fontSize: '10px',
                                            padding: '1px 6px',
                                            background: stage === 'TOFU' ? 'var(--info-100)' :
                                                stage === 'MOFU' ? 'var(--warning-100)' :
                                                    'var(--success-100)',
                                            color: stage === 'TOFU' ? 'var(--info-700)' :
                                                stage === 'MOFU' ? 'var(--warning-700)' :
                                                    'var(--success-700)',
                                            borderRadius: 'var(--radius-sm)'
                                        }}
                                    >
                                        {stage}
                                    </span>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    {onToggleExpanded && (
                        <button
                            onClick={onToggleExpanded}
                            className="btn btn-ghost btn-sm"
                            style={{ padding: 'var(--space-1)' }}
                            title={expanded ? 'Hide guidelines' : 'Show guidelines'}
                        >
                            <Info size={16} />
                        </button>
                    )}
                    {onChangePillar && (
                        <button
                            onClick={onChangePillar}
                            className="btn btn-ghost btn-sm"
                            style={{ fontSize: 'var(--font-size-xs)' }}
                        >
                            Change
                            <ChevronRight size={12} />
                        </button>
                    )}
                    {onClearPillar && (
                        <button
                            onClick={onClearPillar}
                            className="btn btn-ghost btn-sm"
                            style={{ padding: 'var(--space-1)' }}
                            title="Clear pillar selection"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>
            </div>

            {/* Expanded content guidelines */}
            {expanded && (
                <div style={{
                    padding: '0 var(--space-4) var(--space-4)',
                    borderTop: `1px solid ${pillar.color}20`
                }}>
                    {/* Description */}
                    {pillar.description && (
                        <p style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            margin: 'var(--space-3) 0'
                        }}>
                            {pillar.description}
                        </p>
                    )}

                    {/* Content Guidelines Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: 'var(--space-3)',
                        marginTop: 'var(--space-3)'
                    }}>
                        {/* Tone of Voice */}
                        {pillar.contentGuidelines?.toneOfVoice?.length > 0 && (
                            <div>
                                <span style={{
                                    fontSize: '10px',
                                    color: 'var(--color-text-tertiary)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>
                                    Tone of Voice
                                </span>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 'var(--space-1)',
                                    marginTop: 'var(--space-1)'
                                }}>
                                    {pillar.contentGuidelines.toneOfVoice.map((tone, idx) => (
                                        <span
                                            key={idx}
                                            style={{
                                                fontSize: 'var(--font-size-xs)',
                                                padding: '2px 8px',
                                                background: `${pillar.color}15`,
                                                borderRadius: 'var(--radius-full)',
                                                color: pillar.color
                                            }}
                                        >
                                            {tone}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Example Topics */}
                        {pillar.contentGuidelines?.exampleTopics?.length > 0 && (
                            <div>
                                <span style={{
                                    fontSize: '10px',
                                    color: 'var(--color-text-tertiary)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>
                                    Example Topics
                                </span>
                                <ul style={{
                                    margin: 'var(--space-1) 0 0 0',
                                    paddingLeft: 'var(--space-4)',
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    {pillar.contentGuidelines.exampleTopics.slice(0, 4).map((topic, idx) => (
                                        <li key={idx}>{topic}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Target Awareness */}
                        {activeAwarenessStages.length > 0 && (
                            <div>
                                <span style={{
                                    fontSize: '10px',
                                    color: 'var(--color-text-tertiary)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>
                                    Target Audience
                                </span>
                                <div style={{ marginTop: 'var(--space-1)' }}>
                                    {activeAwarenessStages.slice(0, 2).map(stage => (
                                        <div
                                            key={stage.key}
                                            style={{
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-text-secondary)',
                                                marginBottom: '2px'
                                            }}
                                        >
                                            <strong>{stage.label}:</strong> {stage.description}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Key Messages */}
                    {pillar.contentGuidelines?.keyMessages?.length > 0 && (
                        <div style={{ marginTop: 'var(--space-3)' }}>
                            <span style={{
                                fontSize: '10px',
                                color: 'var(--color-text-tertiary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }}>
                                Key Messages
                            </span>
                            <div style={{
                                marginTop: 'var(--space-1)',
                                padding: 'var(--space-2)',
                                background: `${pillar.color}08`,
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                {pillar.contentGuidelines.keyMessages.map((msg, idx) => (
                                    <div key={idx} style={{ marginBottom: idx < pillar.contentGuidelines.keyMessages.length - 1 ? 'var(--space-1)' : 0 }}>
                                        • {msg}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PillarContextBanner;
